import React, { useState, useEffect, useMemo } from 'react';
import { AlertCircle, Wrench, FolderOpen, Clock, Timer, FileText, Target, ClipboardList, BookOpen, Check, Package, X, Loader2, Calendar } from 'lucide-react';
import { useVessel } from '../../../../contexts/VesselContext';
// FT-267 §16: unified canonical category codes (replaces the legacy hard-coded
// list that mixed enum VALUES + an unstructured 'custom' sentinel).
import { CATEGORY_DEFINITIONS } from '../../../../config/maintenanceRequirements-wrapper';
import { generateSequentialId, ID_PREFIXES } from '../../../../utils/sequentialIdGenerator';
import { FFE_TEMPLATES } from '../../../../config/maintenance/templates/ffe.templates';
import { readJSON } from '../../../../lib/storage';
// FT-407: Equipment Register migrated to IndexedDB — read via the IDB-backed hook,
// not localStorage. readJSON() returned [] post-migration, hiding the Link-to-Equipment picker.
import { useEquipmentRegister } from '../../../../hooks/domain/useEquipmentRegister';
import { convertIntervalToDays } from '../../../../utils/intervalHelpers';

// FT-267 §16: sentinel triggers the conditional "Custom category" text input
// — mirrors the performer "Other (specify)…" pattern in LogMaintenanceView.
const OTHER_CATEGORY_VALUE = '__other__';

const AddJobView = ({ onBack, onSave, initialData = null }) => {
  const { getCurrentVessel } = useVessel();
  const currentVessel = getCurrentVessel();
  const [isSaving, setIsSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    // Basic Information
    item: '',
    category: 'custom',
    // FT-267 §16: free-text custom category, populated when category === '__other__'
    customCategory: '',
    interval: 30,
    unit: 'days',

    // Job Details
    requirement: '',
    purpose: '',
    scope: '',

    // Additional Information
    notes: '',
    assignedTo: '',

    // First Due Date (FT-241)
    firstDueOption: 'one_cycle',  // 'one_cycle' | 'due_now' | 'custom'
    customFirstDueDate: '',

    // UI State
    showCategoryDropdown: false
  });

  const [errors, setErrors] = useState({});

  // FT-48: Template picker state
  const [useTemplate, setUseTemplate] = useState(false);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [selectedEquipmentIds, setSelectedEquipmentIds] = useState(initialData?.linkedEquipmentIds || []);

  // FT-48: Load equipment for linking
  // FT-407: source from the IDB-backed Equipment Register hook. `equipment` is the
  // hook's alias for the non-archived combined register (required + user-added).
  const { equipment } = useEquipmentRegister();

  // FT-85 FIX: Load existing maintenance jobs for unique ID generation
  const existingJobs = useMemo(() => {
    const vesselId = currentVessel?.id || 'default';
    return readJSON(`maintenance_jobs_${vesselId}`, []);
  }, [currentVessel?.id]);

  // FT-48: Convert templates to array for display
  const templateList = useMemo(() => {
    return Object.entries(FFE_TEMPLATES).map(([key, template]) => ({
      key,
      ...template
    }));
  }, []);

  // FT-267 §16: unified canonical category list + "Other (specify)…" sentinel.
  // The legacy `{ value: 'custom', label: 'Custom' }` entry has been removed;
  // free-text custom categories now flow through the `__other__` pattern and
  // are stored as the trimmed/lowercased user-entered string. Existing records
  // with category: 'custom' continue to display via getCategoryLabel fallback.
  const categories = useMemo(() => {
    return [
      ...CATEGORY_DEFINITIONS,
      { value: OTHER_CATEGORY_VALUE, label: 'Other (specify)…' },
    ];
  }, []);

  // Populate form when editing
  // FT-189: Load from name || item for canonical field consistency
  useEffect(() => {
    if (initialData) {
      setFormData({
        item: initialData.name || initialData.item || '',
        category: initialData.category || 'custom',
        interval: initialData.interval || 30,
        unit: initialData.unit || 'days',
        requirement: initialData.requirement || '',
        purpose: initialData.jobDescription?.purpose || initialData.purpose || '',
        scope: initialData.jobDescription?.scope || initialData.scope || '',
        notes: initialData.notes || '',
        assignedTo: initialData.assignedTo || '',
        showCategoryDropdown: false
      });
    }
  }, [initialData]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user types
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // FT-48: Apply template to form
  const applyTemplate = (template) => {
    setFormData(prev => ({
      ...prev,
      item: template.title,
      category: template.category?.toLowerCase() || 'custom',
      interval: template.intervalMonths || 12,
      unit: 'months',
      requirement: template.regulation || '',
      purpose: template.description || '',
      scope: template.notes || '',
      notes: template.priority ? `Priority: ${template.priority}` : ''
    }));
    setShowTemplatePicker(false);
    setUseTemplate(false);
  };

  // FT-48: Toggle equipment selection
  const toggleEquipment = (equipmentId) => {
    setSelectedEquipmentIds(prev =>
      prev.includes(equipmentId)
        ? prev.filter(id => id !== equipmentId)
        : [...prev, equipmentId]
    );
  };

  // Format date as YYYY-MM-DD in local timezone (avoids UTC shift from toISOString)
  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Calculate initialNextDue based on user selection
  const calculateInitialNextDue = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    switch (formData.firstDueOption) {
      case 'due_now':
        return formatLocalDate(today);
      case 'custom':
        return formData.customFirstDueDate || null;
      case 'one_cycle':
      default:
        const intervalDays = convertIntervalToDays(parseInt(formData.interval), formData.unit);
        const dueDate = new Date(today);
        dueDate.setDate(dueDate.getDate() + intervalDays);
        return formatLocalDate(dueDate);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields
    if (!formData.item.trim()) {
      newErrors.item = 'Item name is required';
    }
    if (!formData.interval || formData.interval < 1) {
      newErrors.interval = 'Valid interval is required';
    }
    // Validate custom date if selected
    if (formData.firstDueOption === 'custom' && !formData.customFirstDueDate) {
      newErrors.customFirstDueDate = 'Custom date is required';
    }
    // FT-267 §16: when "Other (specify)…" is selected, require a non-trivial
    // free-text value before save.
    if (formData.category === OTHER_CATEGORY_VALUE && formData.customCategory.trim().length < 2) {
      newErrors.customCategory = 'Please enter a custom category';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      // Scroll to first error
      const firstErrorField = Object.keys(errors)[0];
      const element = document.getElementById(firstErrorField);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.focus();
      }
      return;
    }

    // FT-267 §16: resolve the canonical category before save. When the user
    // picked "Other (specify)…", store the trimmed/lowercased free-text — the
    // raw `'__other__'` sentinel must never reach the record.
    const resolvedCategory = formData.category === OTHER_CATEGORY_VALUE
      ? formData.customCategory.trim().toLowerCase()
      : formData.category;

    // Create the job data with flattened structure but also include jobDescription for compatibility
    // FT-189: Sync all name fields - name is canonical, item/equipmentType for legacy compat
    const jobData = {
      // FT-85 FIX: Pass existingJobs to generate unique sequential ID
      id: initialData?.id || generateSequentialId(existingJobs, ID_PREFIXES.MAINTENANCE),
      name: formData.item,
      equipmentType: formData.item,
      item: formData.item,
      category: resolvedCategory,
      interval: parseInt(formData.interval),
      unit: formData.unit,
      requirement: formData.requirement,
      purpose: formData.purpose,
      scope: formData.scope,
      notes: formData.notes,
      assignedTo: formData.assignedTo || null,
      // Include nested jobDescription for backward compatibility
      jobDescription: {
        purpose: formData.purpose,
        scope: formData.scope
      },
      // FT-48: Equipment linking
      linkedEquipmentIds: selectedEquipmentIds,
      // FT-241: Initial next due date for jobs with no completions
      initialNextDue: calculateInitialNextDue(),
      custom: true,
      createdAt: initialData?.createdAt || new Date().toISOString()
    };

    // FT-173: Add loading state like AddCrewView
    setIsSaving(true);
    try {
      await onSave(jobData);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* FT-173: Compact Header - matches AddCrewView */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={onBack}
                className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                title="Close"
              >
                <X className="h-5 w-5" />
              </button>
              <h1 className="text-lg sm:text-xl font-semibold text-gray-900">
                {initialData ? 'Edit Maintenance Job' : 'Add Maintenance Job'}
              </h1>
            </div>
          </div>
        </div>
      </div>

      {/* Form Content */}
      {/* FT-173: Standardized container width to match AddCrewView (max-w-4xl) */}
      {/* FT-83: pb-24 md:pb-0 prevents Save/Cancel buttons from being hidden behind mobile bottom nav */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 pb-24 md:pb-6">
        <form onSubmit={handleSubmit}>
          <div className="space-y-6">

          {/* FT-48: Template Picker Section - Only show when not editing */}
          {!initialData && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200">
              <div className="bg-cyan-600 px-5 py-3 rounded-t-2xl">
                <h3 className="text-base font-semibold text-white flex items-center">
                  <BookOpen className="h-5 w-5 mr-2 text-white" />
                  Start from Template
                </h3>
              </div>
              <div className="px-6 py-5">
                {!showTemplatePicker ? (
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setShowTemplatePicker(true)}
                      className="flex-1 px-4 py-3 text-sm font-medium text-cyan-700 bg-cyan-50 border border-cyan-200 rounded-lg hover:bg-cyan-100 transition-colors"
                    >
                      <BookOpen className="inline h-4 w-4 mr-2" />
                      Choose from Templates
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowTemplatePicker(false)}
                      className="flex-1 px-4 py-3 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <Wrench className="inline h-4 w-4 mr-2" />
                      Create from Scratch
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-gray-600">Select a template to pre-fill the form:</p>
                      <button
                        type="button"
                        onClick={() => setShowTemplatePicker(false)}
                        className="text-sm text-gray-500 hover:text-gray-700"
                      >
                        Cancel
                      </button>
                    </div>
                    <div className="max-h-64 overflow-y-auto space-y-2">
                      {templateList.map((template) => (
                        <button
                          key={template.key}
                          type="button"
                          onClick={() => applyTemplate(template)}
                          className="w-full text-left p-3 rounded-lg border border-gray-200 hover:border-cyan-300 hover:bg-cyan-50 transition-colors"
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-medium text-gray-900">{template.title}</p>
                              <p className="text-sm text-gray-500 mt-0.5">{template.description}</p>
                              <p className="text-xs text-gray-400 mt-1">
                                {template.intervalMonths} months · {template.category} · {template.regulation || 'No regulation'}
                              </p>
                            </div>
                            {template.priority === 'required' && (
                              <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded">Required</span>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Basic Information Section */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200">
            <div className="bg-slate-700 px-5 py-3 rounded-t-2xl">
              <h3 className="text-base font-semibold text-white flex items-center">
                <Wrench className="h-5 w-5 mr-2 text-white" />
                Basic Information
              </h3>
            </div>
            <div className="px-6 py-5">
              <div className="space-y-4">
              {/* Item Name - Full width */}
              <div>
                <label htmlFor="item" className="block text-sm font-medium text-gray-700 mb-1">
                  <Wrench className="inline h-4 w-4 mr-1.5 -mt-0.5 text-cyan-600" aria-hidden="true" />
                  Item Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="item"
                  type="text"
                  value={formData.item}
                  onChange={(e) => handleInputChange('item', e.target.value)}
                  placeholder="e.g., Engine Room Bilge Pump Test"
                  className={`block w-full rounded-md border-0 py-2 px-3 text-gray-900 ring-1 ring-inset ${
                    errors.item ? 'ring-red-300 focus:ring-red-600' : 'ring-gray-300 focus:ring-cyan-600'
                  } placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm`}
                />
                {errors.item && (
                  <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {errors.item}
                  </p>
                )}
              </div>

              {/* Category, Interval, Unit - 3 columns */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {/* Category */}
                <div className="relative">
                  <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                    <FolderOpen className="inline h-4 w-4 mr-1.5 -mt-0.5 text-cyan-600" aria-hidden="true" />
                    Category
                  </label>
                  <div className="relative">
                    <input
                      id="category"
                      type="text"
                      value={categories.find(c => c.value === formData.category)?.label || formData.category}
                      onChange={(e) => {
                        const inputValue = e.target.value;
                        const matchedCategory = categories.find(c => 
                          c.label.toLowerCase() === inputValue.toLowerCase()
                        );
                        if (matchedCategory) {
                          handleInputChange('category', matchedCategory.value);
                        } else {
                          handleInputChange('category', inputValue);
                        }
                      }}
                      onFocus={() => handleInputChange('showCategoryDropdown', true)}
                      onBlur={() => setTimeout(() => handleInputChange('showCategoryDropdown', false), 200)}
                      placeholder="Select category"
                      className="block w-full rounded-md border-0 py-2 px-3 text-gray-900 bg-white ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-cyan-600 sm:text-sm"
                    />
                    
                    {/* Dropdown */}
                    {formData.showCategoryDropdown && (
                      <div className="absolute z-50 mt-1 w-full bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 max-h-60 overflow-auto">
                        <div className="py-1">
                          {categories.map(cat => (
                            <button
                              key={cat.value}
                              type="button"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                handleInputChange('category', cat.value);
                                handleInputChange('showCategoryDropdown', false);
                              }}
                              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                            >
                              {cat.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  {/* FT-267 §16: conditional free-text input when "Other
                      (specify)…" is selected. Mirrors the performer "Other"
                      pattern used in LogMaintenanceView. */}
                  {formData.category === OTHER_CATEGORY_VALUE && (
                    <div className="mt-3">
                      <label htmlFor="customCategory" className="block text-sm font-medium text-gray-700 mb-1">
                        Custom category <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="customCategory"
                        type="text"
                        value={formData.customCategory}
                        onChange={(e) => handleInputChange('customCategory', e.target.value)}
                        placeholder="e.g., Hydraulics"
                        aria-label="Custom category"
                        className={`block w-full rounded-md border-0 py-2 px-3 text-gray-900 ring-1 ring-inset ${
                          errors.customCategory ? 'ring-red-300 focus:ring-red-600' : 'ring-gray-300 focus:ring-cyan-600'
                        } placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm`}
                      />
                      {errors.customCategory && (
                        <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {errors.customCategory}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Interval */}
                <div>
                  <label htmlFor="interval" className="block text-sm font-medium text-gray-700 mb-1">
                    <Clock className="inline h-4 w-4 mr-1.5 -mt-0.5 text-cyan-600" aria-hidden="true" />
                    Interval <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="interval"
                    type="number"
                    min="1"
                    value={formData.interval}
                    onChange={(e) => handleInputChange('interval', e.target.value)}
                    className={`block w-full rounded-md border-0 py-2 px-3 text-gray-900 ring-1 ring-inset ${
                      errors.interval ? 'ring-red-300 focus:ring-red-600' : 'ring-gray-300 focus:ring-cyan-600'
                    } placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm`}
                  />
                  {errors.interval && (
                    <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      {errors.interval}
                    </p>
                  )}
                </div>

                {/* Unit */}
                <div>
                  <label htmlFor="unit" className="block text-sm font-medium text-gray-700 mb-1">
                    <Timer className="inline h-4 w-4 mr-1.5 -mt-0.5 text-cyan-600" aria-hidden="true" />
                    Unit
                  </label>
                  <select
                    id="unit"
                    value={formData.unit}
                    onChange={(e) => handleInputChange('unit', e.target.value)}
                    className="block w-full rounded-md border-0 py-2 px-3 text-gray-900 bg-white ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-cyan-600 sm:text-sm"
                  >
                    <option value="days">Days</option>
                    <option value="weeks">Weeks</option>
                    <option value="months">Months</option>
                    <option value="years">Years</option>
                    <option value="hours">Running Hours</option>
                  </select>
                </div>
              </div>
            </div>
            </div>
          </div>

          {/* First Due Date Section - Only show when adding new job */}
          {!initialData && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200">
              <div className="bg-slate-700 px-5 py-3 rounded-t-2xl">
                <h3 className="text-base font-semibold text-white flex items-center">
                  <Calendar className="h-5 w-5 mr-2 text-white" />
                  First Due Date
                </h3>
              </div>
              <div className="px-6 py-5">
                <p className="text-sm text-gray-600 mb-4">
                  When should this job first become due? This sets the initial due date before any completions are recorded.
                </p>
                <div className="space-y-3">
                  {/* Due in one cycle (default) */}
                  <label className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer border transition-colors ${
                    formData.firstDueOption === 'one_cycle'
                      ? 'bg-cyan-50 border-cyan-200'
                      : 'hover:bg-gray-50 border-gray-200'
                  }`}>
                    <input
                      type="radio"
                      name="firstDueOption"
                      value="one_cycle"
                      checked={formData.firstDueOption === 'one_cycle'}
                      onChange={(e) => handleInputChange('firstDueOption', e.target.value)}
                      className="h-4 w-4 text-cyan-600 focus:ring-cyan-500"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">Due in one cycle</p>
                      <p className="text-xs text-gray-500">
                        First due in {formData.interval} {formData.unit} from today
                      </p>
                    </div>
                  </label>

                  {/* Due now */}
                  <label className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer border transition-colors ${
                    formData.firstDueOption === 'due_now'
                      ? 'bg-cyan-50 border-cyan-200'
                      : 'hover:bg-gray-50 border-gray-200'
                  }`}>
                    <input
                      type="radio"
                      name="firstDueOption"
                      value="due_now"
                      checked={formData.firstDueOption === 'due_now'}
                      onChange={(e) => handleInputChange('firstDueOption', e.target.value)}
                      className="h-4 w-4 text-cyan-600 focus:ring-cyan-500"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">Due now</p>
                      <p className="text-xs text-gray-500">
                        First due today — will show as "Due Soon" immediately
                      </p>
                    </div>
                  </label>

                  {/* Custom date */}
                  <label className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer border transition-colors ${
                    formData.firstDueOption === 'custom'
                      ? 'bg-cyan-50 border-cyan-200'
                      : 'hover:bg-gray-50 border-gray-200'
                  }`}>
                    <input
                      type="radio"
                      name="firstDueOption"
                      value="custom"
                      checked={formData.firstDueOption === 'custom'}
                      onChange={(e) => handleInputChange('firstDueOption', e.target.value)}
                      className="h-4 w-4 text-cyan-600 focus:ring-cyan-500"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">Custom date</p>
                      <p className="text-xs text-gray-500">
                        Set a specific first due date
                      </p>
                    </div>
                  </label>

                  {/* Custom date picker - shows when custom selected */}
                  {formData.firstDueOption === 'custom' && (
                    <div className="ml-7 mt-2">
                      <input
                        type="date"
                        value={formData.customFirstDueDate}
                        onChange={(e) => handleInputChange('customFirstDueDate', e.target.value)}
                        className={`block w-full max-w-xs rounded-md border-0 py-2 px-3 text-gray-900 ring-1 ring-inset ${
                          errors.customFirstDueDate ? 'ring-red-300 focus:ring-red-600' : 'ring-gray-300 focus:ring-cyan-600'
                        } placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm`}
                      />
                      {errors.customFirstDueDate && (
                        <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {errors.customFirstDueDate}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Job Details Section */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200">
            <div className="bg-slate-700 px-5 py-3 rounded-t-2xl">
              <h3 className="text-base font-semibold text-white flex items-center">
                <ClipboardList className="h-5 w-5 mr-2 text-white" />
                Job Details
              </h3>
            </div>
            <div className="px-6 py-5">
              <div className="space-y-4">
              {/* Requirement/Standard */}
              <div>
                <label htmlFor="requirement" className="block text-sm font-medium text-gray-700 mb-1">
                  <FileText className="inline h-4 w-4 mr-1.5 -mt-0.5 text-cyan-600" aria-hidden="true" />
                  Requirement / Standard
                </label>
                <input
                  id="requirement"
                  type="text"
                  value={formData.requirement}
                  onChange={(e) => handleInputChange('requirement', e.target.value)}
                  placeholder="e.g., Company procedure, Manufacturer recommendation"
                  className="block w-full rounded-md border-0 py-2 px-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-cyan-600 sm:text-sm"
                />
              </div>

              {/* Purpose */}
              <div>
                <label htmlFor="purpose" className="block text-sm font-medium text-gray-700 mb-1">
                  <Target className="inline h-4 w-4 mr-1.5 -mt-0.5 text-cyan-600" aria-hidden="true" />
                  Purpose
                </label>
                <input
                  id="purpose"
                  type="text"
                  value={formData.purpose}
                  onChange={(e) => handleInputChange('purpose', e.target.value)}
                  placeholder="Brief description of why this maintenance is needed"
                  className="block w-full rounded-md border-0 py-2 px-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-cyan-600 sm:text-sm"
                />
              </div>

              {/* Scope of Work */}
              <div>
                <label htmlFor="scope" className="block text-sm font-medium text-gray-700 mb-1">
                  <ClipboardList className="inline h-4 w-4 mr-1.5 -mt-0.5 text-cyan-600" aria-hidden="true" />
                  Scope of Work
                </label>
                <textarea
                  id="scope"
                  value={formData.scope}
                  onChange={(e) => handleInputChange('scope', e.target.value)}
                  rows={4}
                  placeholder="Enter tasks, one per line. Use • for bullet points"
                  className="block w-full rounded-md border-0 py-2 px-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-cyan-600 sm:text-sm"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Start each line with • for bullet points
                </p>
              </div>
            </div>
            </div>
          </div>

          {/* FT-48: Equipment Linking Section */}
          {equipment.length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200">
              <div className="bg-slate-700 px-5 py-3 rounded-t-2xl">
                <h3 className="text-base font-semibold text-white flex items-center">
                  <Package className="h-5 w-5 mr-2 text-white" />
                  Link to Equipment
                </h3>
              </div>
              <div className="px-6 py-5">
                <p className="text-sm text-gray-600 mb-3">
                  Select equipment items that this maintenance job applies to. Completions will be recorded against linked equipment.
                </p>
                {selectedEquipmentIds.length > 0 && (
                  <p className="text-sm text-cyan-700 mb-3">
                    <Check className="inline h-4 w-4 mr-1" />
                    {selectedEquipmentIds.length} equipment item{selectedEquipmentIds.length > 1 ? 's' : ''} selected
                  </p>
                )}
                <div className="max-h-48 overflow-y-auto space-y-2 border border-gray-200 rounded-lg p-3">
                  {equipment.map((eq) => (
                    <label
                      key={eq.id}
                      className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                        selectedEquipmentIds.includes(eq.id)
                          ? 'bg-cyan-50 border border-cyan-200'
                          : 'hover:bg-gray-50 border border-transparent'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedEquipmentIds.includes(eq.id)}
                        onChange={() => toggleEquipment(eq.id)}
                        className="h-4 w-4 rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{eq.name}</p>
                        <p className="text-xs text-gray-500 truncate">
                          {eq.category || eq.domain || 'Uncategorized'} {eq.serialNumber ? `· ${eq.serialNumber}` : ''}
                        </p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Additional Information Section */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200">
            <div className="bg-slate-700 px-5 py-3 rounded-t-2xl">
              <h3 className="text-base font-semibold text-white flex items-center">
                <FileText className="h-5 w-5 mr-2 text-white" />
                Additional Notes
              </h3>
            </div>
            <div className="px-6 py-5">
              <div>
              {/* Notes */}
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
                <FileText className="inline h-4 w-4 mr-1.5 -mt-0.5 text-cyan-600" aria-hidden="true" />
                Notes
              </label>
              <textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                rows={3}
                placeholder="Any additional information or special instructions"
                className="block w-full rounded-md border-0 py-2 px-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-cyan-600 sm:text-sm"
              />
            </div>
            {/* FT-183: Assigned to - optional planning field */}
            <div className="mt-4">
              <label htmlFor="assignedTo" className="block text-sm font-medium text-gray-700 mb-1">
                Assigned to <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input
                type="text"
                id="assignedTo"
                value={formData.assignedTo}
                onChange={(e) => handleInputChange('assignedTo', e.target.value)}
                placeholder="e.g. Chief Engineer, John Smith"
                className="block w-full rounded-md border-0 py-2 px-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-cyan-600 sm:text-sm"
              />
            </div>
            </div>
          </div>

            {/* FT-173: Form Actions - in card to match AddCrewView */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 px-6 py-5">
              <div className="flex items-center justify-end gap-3">
                {/* FT-16: min-h-[44px] ensures 44px+ touch targets on mobile */}
                <button
                  type="button"
                  onClick={onBack}
                  className="px-4 py-3 md:py-2 text-base md:text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 min-h-[44px]"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="px-4 py-3 md:py-2 text-base md:text-sm font-medium text-white bg-cyan-600 rounded-md hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 min-h-[44px]"
                >
                  {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
                  {isSaving
                    ? 'Saving...'
                    : initialData ? 'Update Job' : 'Save Job'}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddJobView;